import { useEffect, useState } from 'preact/hooks';
import { getFlair, sendFlair, subscribeFlair, type FlairSlot } from '@/flair';
import { Button, ItemIcon } from '@/ui/components';
import { UI_ITEM_BG, UI_PANEL_BORDER } from '@/ui/consts';
import { useClient } from '@/ui/context';

const SLOTS: { key: FlairSlot; label: string }[] = [
  { key: 'armor', label: 'Armor / costume' }, { key: 'hat', label: 'Hat' }, { key: 'boots', label: 'Shoes' },
];

export function FlairPanel() {
  const client = useClient();
  const [, redraw] = useState(0);
  const [slot, setSlot] = useState<FlairSlot>('armor');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const off = subscribeFlair(client, () => redraw(n => n + 1));
    sendFlair(client, 'flair_view'); return off;
  }, [client, client.characterId, client.mapId]);
  const { snapshot, pending, error } = getFlair(client);
  const selected = snapshot?.selected[slot] ?? 0;
  const choices = snapshot?.choices.filter(i => i.slot === slot && i.name.toLowerCase().includes(search.toLowerCase())) ?? [];
  const name = (id: number) => snapshot?.choices.find(i => i.item === id)?.name ?? client.getEifRecordById(id)?.name ?? 'Item';
  return <section class='space-y-3 text-sm'>
    <p>Wear your owned armor, costumes, hats and shoes as Flair. Your combat gear and stats stay the same.</p>
    <div class='grid grid-cols-3 gap-2'>
      {SLOTS.map(({ key, label }) => {
        const id = snapshot?.selected[key] ?? 0;
        return <div key={key} class={`rounded border ${UI_PANEL_BORDER} ${UI_ITEM_BG} p-2 space-y-2`}>
          <Button variant={['sm', slot === key ? 'primary' : 'ghost']} onClick={() => setSlot(key)}>{label}</Button>
          <p class='break-words'>{id ? name(id) : 'Combat appearance'}</p>
          <Button variant={['xs', 'ghost']} disabled={pending || !id} onClick={() => sendFlair(client, 'flair_unequip', key)}>Remove Flair</Button>
        </div>;
      })}
    </div>
    <div class='flex items-end gap-2'>
      <label class='flex-1'>Find a style<input class='input input-sm input-bordered w-full' value={search} maxLength={80} onInput={e => setSearch(e.currentTarget.value)} /></label>
      <Button variant={['sm']} disabled={pending} onClick={() => sendFlair(client, 'flair_view')}>Refresh</Button>
    </div>
    {error && <p role='alert' class='text-warning'>{error}</p>}
    {pending && <p role='status'>Checking your styles…</p>}
    <div class='space-y-2 max-h-64 overflow-y-auto'>
      {choices.map(choice => <div key={choice.item} class={`flex items-center gap-2 rounded border ${UI_PANEL_BORDER} ${UI_ITEM_BG} p-2`}>
        {choice.graphic > 0 && <ItemIcon key={choice.graphic} graphicId={choice.graphic} alt={choice.name} class='h-9 w-9 object-contain shrink-0' />}
        <span class='flex-1'>{choice.name}</span>
        <Button variant={['sm', selected === choice.item ? 'ghost' : 'primary']} disabled={pending || selected === choice.item}
          onClick={() => sendFlair(client, 'flair_equip', slot, choice.item)}>{selected === choice.item ? 'Wearing' : 'Wear'}</Button>
      </div>)}
      {snapshot && !pending && !choices.length && <p>No matching styles in your inventory or equipment.</p>}
    </div>
    <p class='text-xs opacity-70'>Removing Flair restores your combat appearance. A style clears if you give up its last copy.</p>
  </section>;
}
